import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuthContext } from '../../auth/AuthProvider';
import { supabase } from '../../../utils/supabase';
import { toast } from 'react-hot-toast';
import { 
  Shield, FileText, Calendar, Hash, Loader, 
  Upload, Camera, Fingerprint, AlertCircle,
  CheckCircle, Clock, XCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Database } from '../../../types/supabase';

type KYCDocument = Database['public']['Tables']['kyc_documents']['Insert'];
type KYCBiometric = Database['public']['Tables']['kyc_biometrics']['Insert'];

interface KYCFormData {
  documentType: 'passport' | 'drivers_license' | 'national_id';
  documentNumber: string;
  expiryDate: string;
  documentFile?: FileList;
  biometricType: 'facial' | 'fingerprint';
}

export function KYCTab() {
  const { user, profile } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<KYCFormData>();
  const documentFile = watch('documentFile');

  const onSubmit = async (data: KYCFormData) => {
    if (!user) return;

    try {
      setIsLoading(true);

      // Upload document file if provided
      let documentUrl = null;
      if (documentFile?.[0]) {
        const file = documentFile[0];
        const fileExt = file.name.split('.').pop();
        const filePath = `kyc/${user.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('kyc-documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('kyc-documents')
          .getPublicUrl(filePath);

        documentUrl = publicUrl;
      }

      // Submit KYC document
      const kycData: KYCDocument = {
        user_id: user.id,
        document_type: data.documentType,
        document_number: data.documentNumber,
        expiry_date: data.expiryDate,
        metadata: {
          document_url: documentUrl
        }
      };

      const { error: docError } = await supabase
        .from('kyc_documents')
        .insert(kycData);

      if (docError) throw docError;

      // Submit biometric data
      const biometricData: KYCBiometric = {
        user_id: user.id,
        biometric_type: data.biometricType,
        metadata: {}
      };

      const { error: bioError } = await supabase
        .from('kyc_biometrics')
        .insert(biometricData);

      if (bioError) throw bioError;

      toast.success('KYC documents submitted successfully');
      setCurrentStep(1);
    } catch (err: any) {
      console.error('Error submitting KYC:', err);
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-medium text-gray-900">Document Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Document Type
                </label>
                <select
                  {...register('documentType', { required: 'Document type is required' })}
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="passport">Passport</option>
                  <option value="drivers_license">Driver's License</option>
                  <option value="national_id">National ID</option>
                </select>
                {errors.documentType && (
                  <p className="mt-1 text-sm text-red-600">{errors.documentType.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Document Number
                </label>
                <div className="mt-1 relative rounded-lg shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    {...register('documentNumber', { required: 'Document number is required' })}
                    className="block w-full pl-10 rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter document number"
                  />
                </div>
                {errors.documentNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.documentNumber.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Expiry Date
                </label>
                <div className="mt-1 relative rounded-lg shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    {...register('expiryDate', { required: 'Expiry date is required' })}
                    className="block w-full pl-10 rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {errors.expiryDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.expiryDate.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Upload Document
                </label>
                <div className="mt-1">
                  <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500">
                          <span>Upload a file</span>
                          <input
                            type="file"
                            {...register('documentFile', { required: 'Document file is required' })}
                            className="sr-only"
                            accept="image/*,.pdf"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, PDF up to 10MB
                      </p>
                    </div>
                  </div>
                </div>
                {errors.documentFile && (
                  <p className="mt-1 text-sm text-red-600">{errors.documentFile.message}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Next Step
              </button>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-medium text-gray-900">Biometric Verification</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Verification Method
                </label>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none">
                    <input
                      type="radio"
                      {...register('biometricType')}
                      value="facial"
                      className="sr-only"
                    />
                    <div className="flex flex-1">
                      <div className="flex flex-col">
                        <span className="block text-sm font-medium text-gray-900">Facial Recognition</span>
                        <span className="mt-1 flex items-center text-sm text-gray-500">
                          Verify using your webcam
                        </span>
                      </div>
                    </div>
                    <Camera className="h-5 w-5 text-blue-600" aria-hidden="true" />
                    <div className="pointer-events-none absolute -inset-px rounded-lg border-2" aria-hidden="true" />
                  </label>

                  <label className="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none">
                    <input
                      type="radio"
                      {...register('biometricType')}
                      value="fingerprint"
                      className="sr-only"
                    />
                    <div className="flex flex-1">
                      <div className="flex flex-col">
                        <span className="block text-sm font-medium text-gray-900">Fingerprint</span>
                        <span className="mt-1 flex items-center text-sm text-gray-500">
                          Verify using fingerprint scanner
                        </span>
                      </div>
                    </div>
                    <Fingerprint className="h-5 w-5 text-blue-600" aria-hidden="true" />
                    <div className="pointer-events-none absolute -inset-px rounded-lg border-2" aria-hidden="true" />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Previous Step
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Verification'
                )}
              </button>
            </div>
          </motion.div>
        );
    }
  };

  const renderStatus = () => {
    if (!profile?.kyc_status) {
      return (
        <div className="flex items-center gap-2 text-gray-500">
          <Clock className="w-5 h-5" />
          <span>Not Started</span>
        </div>
      );
    }

    switch (profile.kyc_status) {
      case 'pending':
        return (
          <div className="flex items-center gap-2 text-yellow-600">
            <Clock className="w-5 h-5" />
            <span>Verification in Progress</span>
          </div>
        );
      case 'approved':
        return (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            <span>Verified</span>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex items-center gap-2 text-red-600">
            <XCircle className="w-5 h-5" />
            <span>Verification Failed</span>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            KYC Verification
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Complete your identity verification to unlock all features
          </p>
        </div>
        {renderStatus()}
      </div>

      {profile?.kyc_status === 'approved' ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-green-900">Verification Complete</h3>
              <p className="text-green-700">Your identity has been successfully verified</p>
            </div>
          </div>
          {profile.kyc_verified_at && (
            <p className="text-sm text-green-600">
              Verified on: {new Date(profile.kyc_verified_at).toLocaleDateString()}
            </p>
          )}
        </div>
      ) : profile?.kyc_status === 'rejected' ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-red-900">Verification Failed</h3>
              <p className="text-red-700">{profile.kyc_rejection_reason}</p>
            </div>
          </div>
          <p className="text-sm text-red-600">
            Please submit new documents for verification
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6">
            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex items-center justify-between relative">
                <div className="w-full absolute top-1/2 transform -translate-y-1/2">
                  <div className="h-1 bg-gray-200">
                    <div 
                      className="h-1 bg-blue-500 transition-all duration-300"
                      style={{ width: `${(currentStep - 1) * 100}%` }}
                    />
                  </div>
                </div>
                {[1, 2].map((step) => (
                  <div
                    key={step}
                    className={`relative flex items-center justify-center w-10 h-10 rounded-full ${
                      step <= currentStep ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-sm text-gray-500">Document Upload</span>
                <span className="text-sm text-gray-500">Biometric Verification</span>
              </div>
            </div>

            {/* Step Content */}
            <AnimatePresence mode="wait">
              {renderStepContent()}
            </AnimatePresence>
          </div>
        </form>
      )}
    </div>
  );
}